import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Send, Plane } from "lucide-react";
import api from "../lib/api";
import { VEHICLE_OPTIONS } from "../data/site";
import { DatePicker, TimePicker } from "./DateTimePicker";

const AIRPORTS = [
  { code: "IAD", label: "Dulles International Airport (IAD)" },
  { code: "DCA", label: "Reagan National Airport (DCA)" },
  { code: "BWI", label: "Baltimore/Washington International (BWI)" },
  { code: "Other", label: "Other / Private Jet Terminal" },
];

const empty = {
  full_name: "",
  email: "",
  phone: "",
  pickup_date: "",
  pickup_time: "",
  pickup_location: "",
  dropoff_location: "",
  vehicle_type: "",
  passengers: 1,
  special_requests: "",
  is_airport: false,
  airport: "",
  flight_type: "",
  flight_number: "",
};

export default function BookingForm({ compact = false, eyebrow = "Request a Quick Ride", defaultAirport = false }) {
  const [form, setForm] = useState({ ...empty, is_airport: !!defaultAirport });
  const [loading, setLoading] = useState(false);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-detect airport from pickup/dropoff text
  useEffect(() => {
    const txt = `${form.pickup_location} ${form.dropoff_location}`.toLowerCase();
    const hit = /\b(airport|iad|dca|bwi|dulles|reagan|baltimore[- ]washington)\b/.test(txt);
    if (hit && !form.is_airport) {
      setForm((p) => ({ ...p, is_airport: true }));
    }
  }, [form.pickup_location, form.dropoff_location]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_type) {
      toast.error("Please select a vehicle.");
      return;
    }
    if (form.is_airport && !form.airport) {
      toast.error("Please select an airport.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { ...form, passengers: Number(form.passengers) || 1 });
      toast.success(data.message || "Booking received. We'll confirm shortly.");
      setForm({ ...empty, is_airport: !!defaultAirport });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString?.() || "Could not submit booking. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={`bg-[#0c0c0c] border border-white/10 ${compact ? "p-6" : "p-8 md:p-10"}`}
      data-testid="booking-form"
    >
      <div className="label-eyebrow mb-2">Reserve · 24/7</div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-6">{eyebrow}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Full Name</label>
          <input className="lux-input" required value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} data-testid="book-full-name" />
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Phone</label>
          <input className="lux-input" required value={form.phone} onChange={(e) => onChange("phone", e.target.value)} data-testid="book-phone" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Email</label>
          <input type="email" className="lux-input" required value={form.email} onChange={(e) => onChange("email", e.target.value)} data-testid="book-email" />
        </div>

        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Pick-Up Date</label>
          <DatePicker
            value={form.pickup_date}
            onChange={(v) => onChange("pickup_date", v)}
            testId="book-date"
            placeholder="Select date"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Pick-Up Time</label>
          <TimePicker
            value={form.pickup_time}
            onChange={(v) => onChange("pickup_time", v)}
            testId="book-time"
            placeholder="Select time"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Pick-Up Address</label>
          <input className="lux-input" required placeholder="Enter your text" value={form.pickup_location} onChange={(e) => onChange("pickup_location", e.target.value)} data-testid="book-pickup" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Drop-Off Address</label>
          <input className="lux-input" required placeholder="Enter your text" value={form.dropoff_location} onChange={(e) => onChange("dropoff_location", e.target.value)} data-testid="book-dropoff" />
        </div>

        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Select Vehicle</label>
          <select className="lux-input" required value={form.vehicle_type} onChange={(e) => onChange("vehicle_type", e.target.value)} data-testid="book-vehicle">
            <option value="">Select Vehicle</option>
            {VEHICLE_OPTIONS.map((v) => (<option key={v} value={v}>{v}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Passengers</label>
          <input type="number" min="1" max="56" className="lux-input" value={form.passengers} onChange={(e) => onChange("passengers", e.target.value)} data-testid="book-pax" />
        </div>

        {/* Airport toggle */}
        <div className="md:col-span-2">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_airport}
              onChange={(e) => onChange("is_airport", e.target.checked)}
              className="w-4 h-4 accent-[#9B1B30]"
              data-testid="book-is-airport"
            />
            <span className="text-sm text-white/80 flex items-center gap-2">
              <Plane size={14} className="text-[#d4af6a]" /> This is an airport transfer
            </span>
          </label>
        </div>

        {form.is_airport && (
          <div className="md:col-span-2 border border-[#9B1B30]/30 bg-[#9B1B30]/[0.04] p-5 space-y-4" data-testid="airport-info-section">
            <div className="flex items-center gap-2 mb-1">
              <Plane size={16} className="text-[#d4af6a]" />
              <h4 className="font-display text-lg text-white">Airport Information</h4>
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">
                Airport <span className="text-[#9B1B30]">*</span>
              </label>
              <select
                className="lux-input"
                required={form.is_airport}
                value={form.airport}
                onChange={(e) => onChange("airport", e.target.value)}
                data-testid="book-airport"
              >
                <option value="">Select airport</option>
                {AIRPORTS.map((a) => (<option key={a.code} value={a.code}>{a.label}</option>))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Flight Number</label>
              <input
                className="lux-input"
                placeholder="e.g., UA1234"
                value={form.flight_number}
                onChange={(e) => onChange("flight_number", e.target.value)}
                data-testid="book-flight-number"
              />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Flight Type</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="flight_type"
                    value="arrival"
                    checked={form.flight_type === "arrival"}
                    onChange={(e) => onChange("flight_type", e.target.value)}
                    className="accent-[#9B1B30]"
                    data-testid="book-flight-arrival"
                  />
                  <span className="text-sm text-white/85">Arrival</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="flight_type"
                    value="departure"
                    checked={form.flight_type === "departure"}
                    onChange={(e) => onChange("flight_type", e.target.value)}
                    className="accent-[#9B1B30]"
                    data-testid="book-flight-departure"
                  />
                  <span className="text-sm text-white/85">Departure</span>
                </label>
              </div>
            </div>

            <p className="text-xs text-white/55 italic">
              We monitor all flights to ensure timely pickup.
            </p>
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">
            Additional stops or special requests?
          </label>
          <textarea rows={3} className="lux-input" placeholder="If you need additional stops or requests, please let us know here." value={form.special_requests} onChange={(e) => onChange("special_requests", e.target.value)} data-testid="book-requests" />
        </div>
      </div>

      <button type="submit" disabled={loading} className="btn-accent mt-6 w-full justify-center" data-testid="book-submit">
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        {loading ? "Sending…" : "Reserve My Ride"}
      </button>
      <p className="text-xs text-white/40 mt-4 text-center">
        Need it now? Call dispatch — answered 24/7.
      </p>
    </form>
  );
}
